import { memo } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useApp } from 'hooks';

import {
  getInvoicePdfAsync,
  getInvoiceReceiptPdfAsync,
  getJobDepositInfo,
  getServicePlanInvoicePdfAsync,
  getServicePlanReceiptPdfAsync,
} from 'api';

import { getFullName } from 'helpers/business.helper';
import { getTipBaseAmount } from 'helpers/calculations.helper';

import DateLine from 'components/ui-kit/date-line';
import Breadcrumbs from 'components/ui-kit/breadcrumbs';
import PaymentSummary from 'components/ui-kit/payment-summary';
import PageHeader from 'components/ui-kit/page-header';
import ButtonLink from 'components/ui-kit/buttons/button-link';
import InvoicePayButton from 'components/invoices/invoice-pay-button';
import ServicePlanInvoicePayButton from 'components/service-plan-subscription-payments/pay-button';
import Money from 'components/ui-kit/money';
import RichText from 'components/ui-kit/rich-text';

import InvoiceItemLoader from './loader';
import { getBreadcrumbs } from './utils';
import useInvoiceDetails from './useInvoiceDetails';

import * as S from './styles';

const InvoiceItemDetails = () => {
  const { portal, isCardPaymentsAvailable } = useApp();
  const { id: invoiceId } = useParams();
  const {
    invoice,
    isServicePlanInvoice,
    isLoading,
    isFetched,
    isError,
  } = useInvoiceDetails({
    invoiceId,
    portalId: portal?.id,
  });

  const { data: depositRes } = useQuery(
    ['job-deposit-info', portal?.id, invoiceId],
    getJobDepositInfo,
    {
      enabled: !!portal?.id && !!invoiceId && !isServicePlanInvoice,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
  const deposit = depositRes?.data || { isRequired: false, amount: 0 };

  if (isLoading) {
    return <InvoiceItemLoader />;
  }

  if (isError) {
    return <h1>Error</h1>;
  }

  if (!invoice) {
    return null;
  }

  const {
    company,
    customer,
    jobTitle,
    visibleInvoiceNumber,
    totalAmount,
    taxAmount,
    lineItems,
    taxRate,
    discount,
    sentDate,
    paidDate,
    message,
    billingAddress,
    preCalculatedSummary,
    invoiceSettingsOverride,
    appliedCcSurcharge,
    appliedTip,
    depositPaidAmount,
    amountDue,
    depositPaymentMethod,
    depositPaidDate,
    payments,
    creditToBeApplied,
  } = invoice || {};

  const isPaid = !!paidDate;
  // What is still owed after any deposit or partial payment; what the card is charged for.
  const amountToPay = amountDue ?? totalAmount;
  const tipBaseAmount = getTipBaseAmount(totalAmount, taxAmount);

  const requiredDeposit = !isPaid && deposit.isRequired
    ? {
      amount: deposit.amount,
      amountType: deposit.amountType,
      rawAmount: deposit.rawAmount,
    }
    : null;

  let payButton = <div />;

  if (!isPaid && isCardPaymentsAvailable) {
    payButton = isServicePlanInvoice
      ? (
        <ServicePlanInvoicePayButton
          stretched
          color="primary"
          invoiceId={invoiceId}
          portalId={portal?.id}
          totalAmount={amountToPay}
          tipBaseAmount={tipBaseAmount}
          invoiceSettingsOverride={invoiceSettingsOverride}
          creditToBeApplied={creditToBeApplied}
        />
      )
      : (
        <S.PayButtonStack>
          {deposit.isRequired && (
            <InvoicePayButton
              stretched
              color="primary"
              invoiceId={invoiceId}
              portalId={portal?.id}
              totalAmount={deposit.amount}
              tipBaseAmount={0}
              invoiceSettingsOverride={invoiceSettingsOverride}
              deposit
              buttonLabel={`Pay Deposit $${Number(deposit.amount).toFixed(2)}`}
            />
          )}
          <InvoicePayButton
            stretched
            color={deposit.isRequired ? 'secondary' : 'primary'}
            invoiceId={invoiceId}
            portalId={portal?.id}
            totalAmount={amountToPay}
            tipBaseAmount={tipBaseAmount}
            invoiceSettingsOverride={invoiceSettingsOverride}
            creditToBeApplied={creditToBeApplied}
          />
        </S.PayButtonStack>
      );
  }

  const getInvoiceButton = () => (
    <S.ButtonGroup>
      {isPaid && (
        <ButtonLink
          newTab
          href={isServicePlanInvoice
            ? getServicePlanReceiptPdfAsync(portal?.id, invoiceId)
            : getInvoiceReceiptPdfAsync(portal?.id, invoiceId)}
          icon={<S.DownloadIcon />}
        >
          Download Receipt
        </ButtonLink>
      )}

      <ButtonLink
        newTab
        href={isServicePlanInvoice
          ? getServicePlanInvoicePdfAsync(portal?.id, invoiceId)
          : getInvoicePdfAsync(portal?.id, invoiceId)}
        icon={<S.DownloadIcon />}
      >
        Download Invoice
      </ButtonLink>
    </S.ButtonGroup>
  );

  return (
    isFetched && (
      <>
        <Breadcrumbs
          gap="1rem"
          items={getBreadcrumbs(jobTitle || 'Job')}
        />

        <PageHeader
          title={jobTitle || 'Job'}
          actionItems={getInvoiceButton()}
        />

        <S.Box>
          <S.Header>
            <S.InvoiceNumber>
              <S.TextLine family="bold" size={24}>
                Invoice
              </S.TextLine>

              <S.TextLine family="bold" color="metallic" size={24}>
                {visibleInvoiceNumber}
              </S.TextLine>
            </S.InvoiceNumber>

            <S.AmountDue>
              <S.TextLine color="metallic">Amount due</S.TextLine>

              <S.TextLine family="bold" size={28}>
                <Money amount={amountToPay} />
              </S.TextLine>
            </S.AmountDue>

            <S.CompanyInfo>
              <S.CompanyInfoItem>
                {`${company?.location.location}
                  ${company?.location.unitNumber ? `, ${company?.location.unitNumber}` : ''}`}
              </S.CompanyInfoItem>

              <S.CompanyInfoItem>
                {company?.businessPhoneNumber}
              </S.CompanyInfoItem>

              <S.CompanyInfoItem>{company?.email}</S.CompanyInfoItem>
            </S.CompanyInfo>
          </S.Header>

          <S.Divider />

          <S.General>
            <S.TextGroup area="billedTo">
              <S.TextGroupHeader>Billed to</S.TextGroupHeader>

              <S.TextGroup row>
                <S.TextLine family="semibold">
                  {getFullName(customer)}
                </S.TextLine>

                {customer.ownCompanyName && (
                  <>
                    <S.TextLine>|</S.TextLine>

                    <S.TextLine>{customer.ownCompanyName}</S.TextLine>
                  </>
                )}
              </S.TextGroup>

              <S.TextLine maxWidth="80%">
                {billingAddress.location}
              </S.TextLine>

              {billingAddress.unitNumber && (
                <S.TextLine maxWidth="80%">
                  Unit #:&nbsp;
                  {billingAddress.unitNumber}
                </S.TextLine>
              )}
            </S.TextGroup>

            <S.TextGroup area="number">
              <S.TextGroupHeader>Invoice number</S.TextGroupHeader>

              <S.TextLine>{visibleInvoiceNumber}</S.TextLine>
            </S.TextGroup>

            <S.DatesWrapper>
              <S.TextGroup area="dates">
                <DateLine
                  label="Sent Date"
                  date={sentDate}
                  placeholder={sentDate ? null : 'n/a'}
                />

                <DateLine
                  label="Due Date"
                  date={sentDate}
                  placeholder="Upon receipt"
                />

                <DateLine
                  label="Paid Date"
                  date={paidDate}
                />
              </S.TextGroup>
            </S.DatesWrapper>
          </S.General>

          <S.PaymentSummary>
            <PaymentSummary
              isPaid={isPaid}
              title="Payment Summary"
              lineItems={lineItems}
              taxRate={taxRate}
              discount={discount}
              preCalculatedSummary={preCalculatedSummary || {}}
              displayQuantityAndUnitPrice={!!portal?.invoiceSettings?.displayQuantityAndUnitPrice}
              appliedCcSurcharge={appliedCcSurcharge}
              appliedTip={appliedTip}
              depositPaidAmount={depositPaidAmount}
              amountDue={amountDue}
              depositPaymentMethod={depositPaymentMethod}
              depositPaidDate={depositPaidDate}
              payments={payments}
              requiredDeposit={requiredDeposit}
              creditToBeApplied={creditToBeApplied}
            />
          </S.PaymentSummary>

          <S.PayButton>
            {payButton}
          </S.PayButton>

          {!!message && (
            <S.TextGroup area="message">
              <S.TextGroupHeader>Invoice Notes</S.TextGroupHeader>

              <S.TextLine as="div"><RichText html={message} /></S.TextLine>
            </S.TextGroup>
          )}
        </S.Box>
      </>
    )
  );
};

export default memo(InvoiceItemDetails);
